import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Circuit, CircuitTag, HeatTraceNode, HeatTraceStatus } from '@completion/models';

type RouteIdFields = {
  cpId?: number | string | null;
  cpID?: number | string | null;
  mcpId?: number | string | null;
  mcpID?: number | string | null;
};

type RouteableEntity = (Circuit | CircuitTag) & RouteIdFields;

@Component({
  selector: 'app-circuit-table',
  templateUrl: './circuit-table.component.html',
  styleUrls: ['./circuit-table.component.scss'],
  standalone: false,
})
export class CircuitTableComponent {
  @Input() circuit!: Circuit;
  @Input() isSubCircuit = false;
  @Input() showCubicleName = true;
  @Input() pathPrefix: string[] = [];
  @Input() isMinified = false;
  @Input() focusPath: string[] = [];

  @Output() focusRequested = new EventEmitter<string[]>();

  routeForEntity(entity: RouteableEntity | null | undefined): (string | number)[] | null {
    if (!entity) {
      return null;
    }

    const cpId = this.numericIdForKeys(entity, 'cpId', 'cpID');
    const mcpId = this.numericIdForKeys(entity, 'mcpId', 'mcpID');
    if (cpId == null || mcpId == null) {
      return null;
    }

    return ['/cp', cpId, 'mcp', mcpId];
  }

  entityIsClickable(entity: RouteableEntity | null | undefined): boolean {
    return this.routeForEntity(entity) != null;
  }

  tagQueryParams(tagNumber: string | null | undefined): { tag: string } | null {
    const tag = tagNumber?.trim();
    return tag ? { tag } : null;
  }

  cubicleStatusName(circuit: Circuit): string | undefined {
    const statuses = circuit.tags
      .map((tag) => tag.status)
      .filter((status): status is HeatTraceStatus => !!status && typeof status.rank === 'number' && !!status.name);

    if (statuses.length === 0) {
      return undefined;
    }

    return statuses.reduce((worst, current) => (current.rank > worst.rank ? current : worst)).name;
  }

  displayLabel(label?: string, previousLabel?: string): string {
    if (!label) {
      return '';
    }

    if (!this.isMinified) {
      return label;
    }

    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      return '';
    }

    const trimmedPrevious = previousLabel?.trim();
    const normalizedCurrent = trimmedLabel.replace(/[^a-z0-9]/gi, '').toUpperCase();
    const normalizedPrevious = trimmedPrevious?.replace(/[^a-z0-9]/gi, '').toUpperCase();

    if (
      normalizedPrevious &&
      normalizedCurrent.length === normalizedPrevious.length + 1 &&
      normalizedCurrent.endsWith(normalizedPrevious) &&
      /^[A-Za-z]$/.test(trimmedLabel.charAt(0))
    ) {
      const prefix = trimmedLabel.charAt(0).toUpperCase();
      const previousSuffix = trimmedPrevious
        ?.split('-')
        .filter((part) => part.length > 0)
        .pop();
      return previousSuffix ? `${prefix}-${previousSuffix}` : prefix;
    }

    if (trimmedPrevious && trimmedLabel.startsWith(trimmedPrevious) && trimmedLabel.length > trimmedPrevious.length) {
      return trimmedLabel.slice(trimmedPrevious.length).replace(/^[-_/.:\s]+/, '') || trimmedLabel;
    }

    const parts = trimmedLabel.split('-').filter((part) => part.length > 0);
    if (parts.length > 1) {
      return parts[parts.length - 1];
    }

    if (trimmedLabel.length <= 3) {
      return trimmedLabel;
    }

    return trimmedLabel.slice(-3);
  }

  previousLabelForIndex(index: number): string | undefined {
    if (index > 0) {
      return this.circuit.tags[index - 1]?.no;
    }

    return this.pathPrefix[this.pathPrefix.length - 1];
  }

  requestFocus(jboxNo: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.focusRequested.emit(this.extendPath(this.pathPrefix, jboxNo));
  }

  requestCircuitFocus(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.circuit.no) {
      return;
    }

    let nextPath = [...this.pathPrefix];
    if (this.pathPrefix[this.pathPrefix.length - 1] !== this.circuit.no) {
      nextPath = this.extendPath(this.pathPrefix, this.circuit.no);
    }

    this.focusRequested.emit(nextPath);
  }

  extendPath(path: string[], segment: string): string[] {
    return [...path, segment];
  }

  private numericIdForKeys(entity: RouteIdFields, ...keys: string[]): number | null {
    for (const key of keys) {
      const value = (entity as Record<string, unknown>)[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }

    return null;
  }

  protected readonly HeatTraceNode = HeatTraceNode;
}
